<template>
  <SimplePage title="レスラク接続設定" hideStoreName noIcon>
    <PageHeader title="編集" showBack @back="back">
      <Button @click="back">キャンセル</Button>
      <Button primary @click="save" :disabled="!isChanged">保存</Button>
    </PageHeader>
    <div class="main">
      <dl class="settings detail">
        <dt>予約受付枠名</dt>
        <dd>{{ table.name }}</dd>
        <dt>設定済みテーブル</dt>
        <dd>{{ tableNames(table) }}</dd>
        <dt>定員</dt>
        <dd>{{ formatRange(table) }}名</dd>
        <dt>席タイプ</dt>
        <dd>{{ tableType(table) }}</dd>
        <dt>個室</dt>
        <dd>{{ roomType(table) }}</dd>
        <dt>タバコ</dt>
        <dd>{{ smokingType(table) }}</dd>
        <dt>テーブル結合</dt>
        <dd>{{ seatJoinType(table) }}</dd>
      </dl>
      <dl class="settings">
        <dt class="title">レスラク卓設定</dt>
        <dd>
          <p class="desc">上記の予約受付枠に対し紐づけるレスラク卓を選択し曜日設定を行ってください。</p>
          <DataTable
            class="tables"
            :data="tableList"
            :minItemHeight="56"
            :columns="[
              { key: 'selected', name: '有効' },
              { key: 'name', name: 'レスラク卓名' },
              { key: 'time', name: '時間', filter: ({ time }) => `${time.from}〜${time.to}` },
              { key: 'seatJoin', name: '結合', filter: seatJoinType },
              { key: 'availableSeat', name: '定員', filter: item => `${formatRange(item)}名` },
              { key: 'smoking', name: 'タバコ', filter: smokingType },
            ]"
            :loading="busy"
            :itemClass="({ id }) => !isDisabled(id) || 'disabled'"
          >
            <template #selected="{ item }">
              <CheckBox
                :value="item.id"
                @change="toggleTableId(item.id)"
                :checked="selectedTables.map(val => val.id).includes(item.id)"
                :disabled="isDisabled(item.id)"
              />
            </template>
            <template #name="{ item }">
              <HelpTooltip :message="item.name" container=".vue-recycle-scroller__item-wrapper" scrollHide />
            </template>
            <template #extend="{ item }">
              <div class="days">
                <CheckBox
                  v-for="(val, key) in currentTableDays(item.id)"
                  :key="`${item.id}_${key}`"
                  :checked="!!val.dayValue"
                  @change="toggleTableDays(item.id, val.dayKey)"
                  :disabled="!selectedTables.map(val => val.id).includes(item.id)"
                >
                  {{ val.label }}
                </CheckBox>
              </div>
            </template>
          </DataTable>
        </dd>
      </dl>
    </div>
  </SimplePage>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { formatRange } from '@/utils/number'
import { isEqual, cloneDeep } from 'lodash'
import dayLabelMap from '@/resources/reservation/dayLabelMap'
import typeRoomMap from '@/resources/reservation/typeRoomMap'
import typeTableMap from '@/resources/reservation/typeTableMap'
import typeSmokingMap from '@/resources/reservation/typeSmokingMap'
import SimplePage from '@/components/templates/SimplePage'
import PageHeader from '@/components/molecules/PageHeader'
import DataTable from '@/components/molecules/DataTable'
import Dialog from '@/components/molecules/ModalDialog'
import HelpTooltip from '@/components/molecules/HelpTooltip'
import Button from '@/components/atoms/Button'
import CheckBox from '@/components/atoms/CheckBox'

export default {
  name: 'Resraku',
  components: {
    SimplePage,
    PageHeader,
    DataTable,
    HelpTooltip,
    Button,
    CheckBox,
  },
  props: {
    table: { type: Object },
  },
  data() {
    return {
      selectedTables: [],
      initTables: [],
      isChanged: false,
      dayLabelMap,
    }
  },
  computed: {
    ...mapState({
      tableList: state => state.resraku.table.tableList,
      busy: state => state.resraku.table.busy,
    }),
    ...mapGetters({
      resrakuTableIds: 'resraku/list/selectedResrakuTableIds',
    }),
    currentTableIds() {
      return this.table.resrakuTables.map(v => v.id)
    },
  },
  watch: {
    selectedTables: {
      handler() {
        if (isEqual(this.selectedTables, this.initTables)) {
          this.isChanged = false
        } else {
          this.isChanged = true
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.$store.dispatch('resraku/table/get')
    this.selectedTables = this.table.resrakuTables.map(val => ({ id: val.id, day: val.day }))
    this.initTables = cloneDeep(this.selectedTables)
  },
  methods: {
    formatRange,
    tableNames: ({ tableNames }) => tableNames.join('、'),
    tableType: ({ tableType }) => typeTableMap[tableType],
    roomType: ({ privRoomType }) => typeRoomMap[privRoomType],
    smokingType: ({ smokingType }) => typeSmokingMap[smokingType],
    seatJoinType: ({ seatJoinFlg }) => ['不可', '可'][seatJoinFlg],
    isDisabled(id) {
      return this.resrakuTableIds.filter(v => !this.currentTableIds.includes(v)).includes(id)
    },
    currentTable(id) {
      return this.selectedTables.find(val => val.id === id)
    },
    currentTableDays(id) {
      return [1, 2, 3, 4, 5, 6, 0, -1, -2].map(day => ({
        dayKey: day,
        label: { 0: '日', 1: '月', 2: '火', 3: '水', 4: '木', 5: '金', 6: '土', '-1': '祝日', '-2': '祝前日' }[day],
        dayValue: this.currentTable(id)?.day[day] || 0,
      }))
    },
    toggleTableId(id) {
      if (!this.currentTable(id)) {
        this.selectedTables.push({
          id,
          day: { '1': 1, '2': 1, '3': 1, '4': 1, '5': 1, '6': 1, '0': 1, '-1': 1, '-2': 1 },
        })
      } else {
        this.selectedTables = this.selectedTables.filter(val => val.id !== id)
      }
    },
    toggleTableDays(id, key) {
      this.currentTable(id).day[key] = Number(!this.currentTable(id).day[key])
    },
    async save() {
      if (
        !(await Dialog.confirm({
          title: 'レスラク接続設定',
          message: '保存してよろしいですか？',
          okText: '保存',
          cancelText: '戻る',
        }))
      ) {
        return
      }

      await this.$store.dispatch('resraku/regist/save', {
        sync_grouping_gnavi_id: this.table.id,
        resraku_tables: this.selectedTables,
      })
      this.back()
    },
    back() {
      location.href = '/resraku/'
    },
  },
}
</script>
<style lang="postcss" scoped>
.main {
  background: var(--white);
  margin: 16px 32px 32px;
  padding: 32px;
}

.settings {
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 108px 1fr;

  &.detail {
    grid-template-columns: 120px 1fr;
  }

  dt {
    font-weight: 600;
  }

  dd {
    font-size: 16px;
  }

  & + & {
    border-top: solid 1px var(--border-color);
    margin-top: 32px;
    padding-top: 32px;
  }
}

.desc {
  font-size: 15px;
  margin-bottom: 24px;
}

.tables {
  font-size: 15px;

  >>> .row {
    border-left: solid 1px var(--border-color);
    border-right: solid 1px var(--border-color);
  }

  >>> tr {
    display: grid;
    grid-template:
      'selected name time seatJoin availableSeat smoking' 56px
      'selected days days days days days' 56px /
      58px 1fr 136px 64px 120px 96px;
  }

  >>> td {
    justify-content: center;
    padding: 0 14px;

    &.name {
      justify-content: left;
    }

    &:not(:last-child)::after {
      right: 1px;
    }
  }

  >>> .selected {
    grid-area: selected;
    min-width: 58px;
    width: 58px;
  }

  >>> .name {
    grid-area: name;
    min-width: 288px;
  }

  >>> .time {
    grid-area: time;
    min-width: 136px;
    width: 136px;
  }

  >>> .seatJoin {
    grid-area: seatJoin;
    min-width: 64px;
    width: 64px;
  }

  >>> .availableSeat {
    grid-area: availableSeat;
    min-width: 120px;
    width: 120px;
  }

  >>> .smoking {
    grid-area: smoking;
    min-width: 96px;
    width: 96px;

    &::after {
      content: none;
    }
  }

  >>> .days {
    align-items: center;
    background: var(--mostly-white);
    border-top: solid 1px var(--border-color);
    display: grid;
    grid-area: days;
    grid-auto-flow: column;
    padding: 0 16px;

    .check-box {
      align-items: center;
    }

    .label {
      padding: 0;
    }

    svg,
    .box {
      height: 24px;
      width: 24px;
    }
  }
}
</style>
